// frontend/features/crm/crmSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchCrmPipelines,
  updateCrmPipeline,
  fetchCrmContacts,
  createCrmContact,
  updateCrmContact,
  deleteCrmContact,
  fetchCrmServices,
  createCrmService,
  updateCrmService,
  deleteCrmService,
  fetchCrmDeals,
  createCrmDeal,
  updateCrmDeal,
  deleteCrmDeal,
  fetchCrmActivities,
  createCrmActivity,
  updateCrmActivity,
  deleteCrmActivity,
  fetchCrmCommunications,
  fetchCrmWhatsAppMessages,
  sendCrmWhatsAppMessage,
} from "./crmThunks.js";

const initialState = {
  pipelines: [],
  contacts: [],
  services: [],
  deals: [],
  activities: [],
  communications: [],
  whatsappMessages: [],
  loading: false,
  error: null,
};

const extractResults = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.results)) return payload.results;
  return [];
};

const crmSlice = createSlice({
  name: "crm",
  initialState,
  reducers: {
    clearCrmError(state) {
      state.error = null;
    },
    clearWhatsAppMessages(state) {
      state.whatsappMessages = [];
    },
  },
  extraReducers: (builder) => {
    // Pipelines
    builder
      .addCase(fetchCrmPipelines.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCrmPipelines.fulfilled, (state, action) => {
        state.loading = false;
        state.pipelines = extractResults(action.payload);
      })
      .addCase(fetchCrmPipelines.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al cargar pipelines" };
      });

    // ✅ Pipeline update (delegación / assigned_users)
    builder
      .addCase(updateCrmPipeline.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCrmPipeline.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const index = state.pipelines.findIndex((pipeline) => pipeline.id === updated.id);
        if (index !== -1) {
          state.pipelines[index] = updated;
        } else {
          state.pipelines.unshift(updated);
        }
      })
      .addCase(updateCrmPipeline.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al actualizar pipeline" };
      });

    // CONTACTOS - fetch
    builder
      .addCase(fetchCrmContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCrmContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = extractResults(action.payload);
      })
      .addCase(fetchCrmContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al cargar contactos" };
      });

    // CONTACTOS - create
    builder
      .addCase(createCrmContact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCrmContact.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) state.contacts.unshift(action.payload);
      })
      .addCase(createCrmContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al crear contacto" };
      });

    // CONTACTOS - update
    builder
      .addCase(updateCrmContact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCrmContact.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const index = state.contacts.findIndex((contact) => contact.id === updated.id);
        if (index !== -1) state.contacts[index] = updated;
      })
      .addCase(updateCrmContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al actualizar contacto" };
      });

    // CONTACTOS - delete
    builder
      .addCase(deleteCrmContact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCrmContact.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload;
        state.contacts = state.contacts.filter((contact) => contact.id !== deletedId);
      })
      .addCase(deleteCrmContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al eliminar contacto" };
      });

    // SERVICIOS - fetch
    builder
      .addCase(fetchCrmServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCrmServices.fulfilled, (state, action) => {
        state.loading = false;
        state.services = extractResults(action.payload);
      })
      .addCase(fetchCrmServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al cargar servicios" };
      });

    // SERVICIOS - create
    builder
      .addCase(createCrmService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCrmService.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) state.services.unshift(action.payload);
      })
      .addCase(createCrmService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al crear servicio" };
      });

    // SERVICIOS - update
    builder
      .addCase(updateCrmService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCrmService.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const index = state.services.findIndex((service) => service.id === updated.id);
        if (index !== -1) state.services[index] = updated;
      })
      .addCase(updateCrmService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al actualizar servicio" };
      });

    // SERVICIOS - delete
    builder
      .addCase(deleteCrmService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCrmService.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload;
        state.services = state.services.filter((service) => service.id !== deletedId);
      })
      .addCase(deleteCrmService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al eliminar servicio" };
      });

    // DEALS
    builder
      .addCase(fetchCrmDeals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCrmDeals.fulfilled, (state, action) => {
        state.loading = false;
        state.deals = extractResults(action.payload);
      })
      .addCase(fetchCrmDeals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al cargar deals" };
      })
      .addCase(createCrmDeal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCrmDeal.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) state.deals.unshift(action.payload);
      })
      .addCase(createCrmDeal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al crear el caso" };
      })
      .addCase(updateCrmDeal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCrmDeal.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const index = state.deals.findIndex((deal) => deal.id === updated.id);
        if (index !== -1) state.deals[index] = updated;
      })
      .addCase(updateCrmDeal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al actualizar el caso" };
      })
      .addCase(deleteCrmDeal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCrmDeal.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload;
        state.deals = state.deals.filter((deal) => deal.id !== deletedId);
      })
      .addCase(deleteCrmDeal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al eliminar el caso" };
      });

    // ACTIVIDADES
    builder
      .addCase(fetchCrmActivities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCrmActivities.fulfilled, (state, action) => {
        state.loading = false;
        state.activities = extractResults(action.payload);
      })
      .addCase(fetchCrmActivities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al cargar actividades" };
      })
      .addCase(createCrmActivity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCrmActivity.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) state.activities.unshift(action.payload);
      })
      .addCase(createCrmActivity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al crear actividad" };
      })
      .addCase(updateCrmActivity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCrmActivity.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const index = state.activities.findIndex((activity) => activity.id === updated.id);
        if (index !== -1) state.activities[index] = updated;
      })
      .addCase(updateCrmActivity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al actualizar actividad" };
      })
      .addCase(deleteCrmActivity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCrmActivity.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload;
        state.activities = state.activities.filter((activity) => activity.id !== deletedId);
      })
      .addCase(deleteCrmActivity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al eliminar actividad" };
      });

    // COMUNICACIONES
    builder
      .addCase(fetchCrmCommunications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCrmCommunications.fulfilled, (state, action) => {
        state.loading = false;
        state.communications = extractResults(action.payload);
      })
      .addCase(fetchCrmCommunications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al cargar comunicaciones" };
      });

    // WHATSAPP
    builder
      .addCase(fetchCrmWhatsAppMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCrmWhatsAppMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.whatsappMessages = extractResults(action.payload);
      })
      .addCase(fetchCrmWhatsAppMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al cargar WhatsApp" };
      });

    builder
      .addCase(sendCrmWhatsAppMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendCrmWhatsAppMessage.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.whatsappMessages.unshift(action.payload);
          state.communications.unshift(action.payload);
        }
      })
      .addCase(sendCrmWhatsAppMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { detail: "Error al enviar WhatsApp" };
      });
  },
});

export const { clearCrmError, clearWhatsAppMessages } = crmSlice.actions;
export default crmSlice.reducer;